import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ErrorRecoveryDemo.module.css';

type Stage = 'failed' | 'retrying' | 'notify' | 'qr' | 'ready';

export function ErrorRecoveryDemo() {
  const [stage, setStage] = useState<Stage>('failed');
  const [via, setVia] = useState<'retry' | 'notify' | null>(null);
  const [feedback, setFeedback] = useState(
    'The carrier is down, but the return is saved. Pick a way forward.'
  );
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const retry = () => {
    setStage('retrying');
    setVia('retry');
    setFeedback('Simulating a retry five minutes later. The return details stay put either way.');
    timer.current = window.setTimeout(() => {
      setStage('ready');
      setFeedback('The error was temporary, so retrying worked. Nothing had to be re-entered.');
    }, 1600);
  };

  const notify = () => {
    setStage('notify');
    setVia('notify');
    setFeedback('You can walk away. Try bringing the carrier back online to see the follow-up.');
  };

  const carrierBack = () => {
    setStage('ready');
    setFeedback('The agent followed up on its own, using the return you already saved.');
  };

  const useQr = () => {
    setStage('qr');
    setFeedback('A different path that skips the broken service entirely - no label or printer needed.');
  };

  const labelChip =
    stage === 'ready' ? (
      <span className={`${kit.chip} ${kit.chipGood}`}>Label ready</span>
    ) : stage === 'qr' ? (
      <span className={`${kit.chip} ${kit.chipGood}`}>Drop-off code</span>
    ) : stage === 'retrying' ? (
      <span className={kit.chip}>Retrying...</span>
    ) : (
      <span className={`${kit.chip} ${kit.chipWarn}`}>Carrier not responding</span>
    );

  return (
    <div className={kit.root}>
      <div className={kit.message}>Start a return for the hiking boots, they're too small.</div>

      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✧</span>
        <p>
          I've saved your return for the boots. I couldn't create the shipping label - the carrier's
          system isn't responding right now. This is usually temporary.
        </p>
      </div>

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>Return saved</span>
          <span>RMA 20417</span>
        </div>
        <h3 className={kit.proposalTitle}>Ridgeline hiking boots</h3>
        <dl className={kit.fields}>
          <dt>Size</dt>
          <dd>US 9</dd>
          <dt>Reason</dt>
          <dd>Too small</dd>
          <dt>Refund</dt>
          <dd>$139.00 to original card</dd>
          <dt>Label</dt>
          <dd>{labelChip}</dd>
        </dl>

        {stage === 'failed' && (
          <div className={`${kit.row} ${styles.actions}`}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={retry}>
              Try again in 5 min
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={notify}>
              Notify me when ready
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={useQr}>
              Use a drop-off QR code
            </button>
          </div>
        )}
      </div>

      {stage === 'notify' && (
        <>
          <div className={kit.bubbleUser}>Notify me when ready.</div>
          <div className={kit.bubbleAgent}>
            Will do. Your return is saved, and I'll message you with the label as soon as the carrier is back.
          </div>
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={carrierBack}>
              Simulate: carrier back online
            </button>
          </div>
        </>
      )}

      {stage === 'retrying' && (
        <>
          <div className={kit.bubbleUser}>Try again in 5 min.</div>
          <div className={kit.bubbleAgent}>Okay. Five minutes later - trying the carrier again...</div>
        </>
      )}

      {stage === 'ready' && (
        <>
          {via === 'notify' ? (
            <div className={kit.bubbleUser}>Notify me when ready.</div>
          ) : (
            <div className={kit.bubbleUser}>Try again in 5 min.</div>
          )}
          <div className={kit.bubbleAgent}>
            {via === 'notify'
              ? 'Good news - the carrier is back. Your label for the boots is ready to print. The return reason and refund are unchanged.'
              : 'Tried again and it worked. Your label for the boots is ready to print, using the details you already gave.'}
          </div>
        </>
      )}

      {stage === 'qr' && (
        <>
          <div className={kit.bubbleUser}>Use a drop-off QR code.</div>
          <div className={kit.bubbleAgent}>
            Here's your drop-off code. Show it at any Parcelpoint counter and they'll print the label for you.
          </div>
          <div className={styles.code}>
            <span className={kit.label}>Drop-off code</span>
            <span className={styles.codeValue}>RTN-48K2-9QX</span>
          </div>
        </>
      )}

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
